import z from 'zod';
import {
  ApplePayConfiguration,
  BrandAgreement,
  GooglePayConfiguration,
  Maybe,
  PayAtPump,
  PayInside,
  KountConfiguration,
  PinCodeConfiguration,
  WalletConfiguration as GraphQLWalletConfiguration,
  DeleteAccountConfiguration,
  PointsStrategy,
  PointExpirationStrategy,
  GetAppConfigQuery,
  CommunicationPreferenceConfiguration,
  EaivAgeVerificationConfiguration,
  EaivIdentityVerificationConfiguration,
  ProductInterestsConfiguration,
  AppRatingConfiguration,
  AuthMethod,
  SmsOptInConfiguration,
  OtpChannel,
  MobilePaymentsReceiptsViewConfiguration,
  StoreLocatorConfiguration as StoreLocatorRemoteConfiguration,
  ReferralProgramConfigFragment,
  OrderAheadConfiguration,
  OrderAheadConfigurationProviderVroomFragment,
  OrderAheadConfigurationProviderOloFragment,
  ExternalIdentityVerificationProvider,
} from '@ocx/graphql';
import { LoyaltyCurrencyConfig } from '@ocx/feat-loyalty-currency';
import { SupportConfiguration } from './sub-configurations/support/support-configuration.types';
import { FuelGradesConfigurationSchema, LocalRetailerConfigurationSchema } from './app-config.schema';

export type RemoteConfiguration = GetAppConfigQuery['appConfig'];

export type LoyaltyProgramConfiguration = {
  // TODO: deprecate LoyaltyProgramConfiguration.pointsStrategy
  /**
   * @deprecated
   * Please use loyaltyCurrency.pointsStrategy instead
   */
  pointsStrategy: PointsStrategy;
  pointExpiration: PointExpirationConfiguration;
  homePageOffersLimit: number;
  redeemOfferWithCompletedProfileOnly: boolean;
  membershipMinAge: number;
  membershipMaxAge: number;
  isExplicitTermsAcceptanceRequired: boolean;
  isExplicitSmsPromotionAcceptanceEnabled: boolean;
  isPromocodesEnabled: boolean;
  referralProgramConfig: ReferralProgramConfigFragment | null;
};

type StoreLocatorRuntimeConfiguration = {
  googleMapsApiKey: string;
  defaultLocation: {
    lat: number;
    lng: number;
  };
  watchTimeout: number;
  atTheStoreDistance: number;
  fuelPricesMode: StoreLocatorRemoteConfiguration['fuelPricesMode'];
};

export type RuntimeConfig = {
  release: string;
  build: string;
  version: string;
  retailer: ISchemaConfig['retailer'];
  brandUuid: string;
  chainId: string;
  subBrandName: ISchemaConfig['subBrandName'];
  subBrandCode: ISchemaConfig['subBrandCode'];

  termsConditions: BrandAgreement;
  privacyPolicy: BrandAgreement;
  support: SupportConfiguration;
  loyaltyProgram: LoyaltyProgramConfiguration;
  loyaltyCurrency: LoyaltyCurrencyConfig;
  payAtPump: PayAtPump;
  payInside: PayInside;
  transaction: {
    pollInterval: number;
    errorLimit: number;
  };
  applePay: Maybe<ApplePayConfiguration>;
  googlePay: Maybe<GooglePayConfiguration>;
  marketing: {
    title: ISchemaConfig['marketing']['title'];
  };
  rudderStack: ISchemaConfig['rudderStack'] & {
    enabled: boolean;
  };
  totalAvailableRollback: {
    enabled: boolean;
  };
  localRetailerConfig: LocalRetailerConfiguration;

  kount: Maybe<KountConfiguration>;
  pinCode: Partial<Pick<PinCodeConfiguration, 'blockingDuration' | 'attemptThrottle' | 'maxAttempts'>> & {
    pinInMemoryTtl: number;
  };
  wallet: WalletConfiguration;
  deleteAccount: DeleteAccountConfiguration;
  paymentProvider: PaymentProviderConfiguration;
  communicationPreference: CommunicationPreferenceConfiguration;
  eaivAgeVerification: EaivAgeVerificationConfiguration;
  eaivIdentityVerification: EaivIdentityVerificationConfiguration;
  productInterests: ProductInterestsConfiguration;
  appRating: AppRatingConfiguration;
  auth: {
    primaryMethod: AuthMethod;
    otp: {
      enabled: boolean;
      channels: OtpChannel[];
    };
  };
  smsOptIn: SmsOptInConfiguration;
  mobilePaymentsReceiptsView: MobilePaymentsReceiptsViewConfiguration;
  storeLocator: StoreLocatorRuntimeConfiguration;
  smsOptedOutWarning: SmsOptedOutWarningConfiguration;
  orderAhead:
    | {
        enabled: true;
        navbar: OrderAheadConfiguration['navbar'];
        provider: OrderAheadProviderConfiguration;
      }
    | { enabled: false };
  externalIdentityVerification: { enabled: false } | { enabled: true; provider: ExternalIdentityVerificationProvider };
  personalDataShareOptOut: { enabled: false } | { enabled: true; optOutUrl: string };
  appsFlyer: AppsFlyerConfiguration;
  localOffers:
    | {
        enabled: true;
        radiusInMeters: number;
      }
    | { enabled: false };
  assets: Record<AssetKey, string>;
};

export type FuelGradesConfiguration = z.infer<typeof FuelGradesConfigurationSchema>;
export type LocalRetailerConfiguration = z.infer<typeof LocalRetailerConfigurationSchema>;

export enum PAYMENT_PROVIDER_TYPE {
  FISERV = 'FISERV',
  WORLDPAY = 'WORLDPAY',
  PAR_PAY = 'PAR_PAY',
}
export interface PaymentProviderBaseConfiguration {
  type: PAYMENT_PROVIDER_TYPE;
}
export interface PaymentProviderFiservConfiguration extends PaymentProviderBaseConfiguration {
  type: PAYMENT_PROVIDER_TYPE.FISERV;
}
export interface PaymentProviderParPayConfiguration extends PaymentProviderBaseConfiguration {
  type: PAYMENT_PROVIDER_TYPE.PAR_PAY;
}
export interface PaymentProviderWorldpayConfiguration extends PaymentProviderBaseConfiguration {
  type: PAYMENT_PROVIDER_TYPE.WORLDPAY;
  eProtectPayPageId: string;
  eProtectGooglePayPageId: string;
  eProtectWebSdkApiUrl: string;
  eProtectWebSdkUrl: string;
}
export type PaymentProviderConfiguration =
  | PaymentProviderFiservConfiguration
  | PaymentProviderParPayConfiguration
  | PaymentProviderWorldpayConfiguration
  | null;

export const ORDER_AHEAD_PROVIDER_TYPE_OLO = 'olo';
export const ORDER_AHEAD_PROVIDER_TYPE_VROOM = 'vroom';

export type OrderAheadProviderVroomConfiguration = {
  type: typeof ORDER_AHEAD_PROVIDER_TYPE_VROOM;
} & OrderAheadConfigurationProviderVroomFragment;
export type OrderAheadProviderOloConfiguration = {
  type: typeof ORDER_AHEAD_PROVIDER_TYPE_OLO;
} & OrderAheadConfigurationProviderOloFragment;

// Extend with other providers when needed
export type OrderAheadProviderConfiguration = OrderAheadProviderVroomConfiguration | OrderAheadProviderOloConfiguration;

export interface WalletConfiguration extends GraphQLWalletConfiguration {
  sizeLimit: number;
  prepaidAccountTypeEnabled: boolean;
}

export type PointExpirationConfiguration = {
  strategy: PointExpirationStrategy;
  notificationDays: number | null;
  isFifo: boolean;
  isActivityBased: boolean;
  isNone: boolean;
};

export type SmsOptedOutWarningConfiguration = {
  enabled: boolean;
  smsOptInPhoneNumber: string;
  smsOptInKeyword: string;
};

export type AppsFlyerConfiguration =
  | {
      enabled: true;
      appId: string;
      devKey: string;
      webDevKey: string | null;
    }
  | { enabled: false };

/**
 * All supported asset keys used across configuration.
 */
export type AssetKey =
  | 'Achievement'
  | 'FuelingCompleted'
  | 'FuelingProcess'
  | 'NavLogo'
  | 'MapMarker'
  | 'HowDoesItWorkIcon'
  | 'UserNotVerifiedIcon'
  | 'UserVerifiedIcon'
  | 'CoinIcon'
  | 'CoinsIcon'
  | 'DollarCoinIcon'
  | 'DollarsCoinsIcon'
  | 'Medal'
  | 'AgeRestricted'
  | 'NoData'
  | 'ReadyToUseRewards'
  | 'TotalAvailableRollback'
  | 'PrepaidIcon'
  | 'ForceUpgradeBackground'
  | 'VisaIcon'
  | 'MastercardIcon'
  | 'DiscoverIcon'
  | 'AmexIcon'
  | 'ApplePayIcon'
  | 'GooglePayIcon'
  | 'GeneralCardIcon'
  | 'SplitPaymentIcon'
  | 'UnknownPaymentInstrumentIcon'
  | 'CashIcon';
